"use client";

import React, { useEffect, useState } from "react";
import { getLandingTenantsService, LandingTenant } from "@/src/services/tenant/tenant.service";

const COLORS = [
  { bg: "rgba(228,96,78,0.15)",  color: "var(--rising-sun)" },
  { bg: "rgba(53,73,48,0.15)",   color: "var(--chutney-green)" },
  { bg: "rgba(8,61,118,0.15)",   color: "var(--indigo)" },
  { bg: "rgba(199,124,68,0.18)", color: "var(--chai-tea)" },
];

interface MoreNeighborsProps {
  currentPrefix: string;
}

export const MoreNeighbors: React.FC<MoreNeighborsProps> = ({ currentPrefix }) => {
  const [tenants, setTenants] = useState<LandingTenant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLandingTenantsService()
      .then((all) => setTenants(all.filter((t) => t.prefix?.toLowerCase() !== currentPrefix.toLowerCase())))
      .finally(() => setLoading(false));
  }, [currentPrefix]);

  if (loading) return null;
  if (tenants.length === 0) return null;

  const openShop = (prefix: string) => {
    const { protocol, host, port } = window.location;
    // host is e.g. "sugandhi.lvh.me:3000" — drop the first subdomain segment
    const baseDomain = host.replace(/^[^.]+\./, "");
    const url = `${protocol}//${prefix}.${baseDomain}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="mt-[clamp(40px,5vw,64px)]">
      {/* Section head */}
      <div className="mb-[clamp(16px,2vw,24px)] flex items-end justify-between gap-4">
        <div>
          <h2
            className="font-bold leading-tight"
            style={{ fontSize: "clamp(18px,2.5vw,24px)", letterSpacing: "-0.5px" }}
          >
            More from your neighbourhood
          </h2>
        </div>
        <button
          className="flex shrink-0 items-center gap-1.5 text-sm font-semibold whitespace-nowrap transition"
          style={{ color: "var(--techno-black-70)" }}
          onMouseEnter={e => (e.currentTarget.style.color = "var(--techno-black)")}
          onMouseLeave={e => (e.currentTarget.style.color = "var(--techno-black-70)")}
        >
          View all near you
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-[clamp(12px,1.6vw,20px)] sm:grid-cols-2 lg:grid-cols-4">
        {tenants.map((tenant, i) => {
          const { bg, color } = COLORS[i % COLORS.length];
          const displayName = tenant.outlets?.[0]?.outletName || tenant.tenantName;
          const initial = displayName.charAt(0).toUpperCase();
          return (
            <button
              key={tenant.tenantId}
              onClick={() => openShop(tenant.prefix)}
              className="flex items-center gap-3 rounded-[14px] p-4 text-left transition-all"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(40,30,20,0.06)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = "";
              }}
            >
              {/* Avatar — logo or initial */}
              <div
                className="flex shrink-0 items-center justify-center rounded-xl text-xl font-bold overflow-hidden"
                style={{ width: 48, height: 48, background: bg, color }}
              >
                {tenant.additionalInfo?.logoUrl ? (
                  <img
                    src={tenant.additionalInfo.logoUrl}
                    alt={tenant.tenantName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  initial
                )}
              </div>
              {/* Info */}
              <div className="min-w-0">
                <div
                  className="truncate font-semibold leading-snug"
                  style={{ fontSize: 14, color: "var(--techno-black)" }}
                >
                  {displayName}
                </div>
                {tenant.businessType?.businessTypeName && (
                  <div
                    className="mt-0.5 truncate"
                    style={{ fontSize: "11.5px", color: "var(--techno-black-50)" }}
                  >
                    {tenant.businessType.businessTypeName}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};
